import axios from "axios";
import { useMutation, useQueryClient } from "react-query";

/**
 * Bulk-delete datasets by FE-looping the existing single-dataset endpoint
 * (DELETE /api/content/deleteDataset/:contentType). Each call wipes one
 * dataset's documents, annotations, AI categorizations, summaries, chunks,
 * ES indices, role permissions, and tpl config — see deleteDataset.js BE.
 *
 * Returns an aggregate { results, errors, success, failed } shape so the
 * caller can show a single summary banner. Per-dataset banners would spam
 * the user when N is large.
 *
 * Backend gates each call on contentAccess(["DELETE"]) against the URL's
 * contentType — datasets the user can't delete come back as failures and
 * are surfaced in the summary, not silently dropped.
 */
export const useBulkDeleteDatasets = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ contentTypes }) => {
      const settled = await Promise.allSettled(
        contentTypes.map((ct) =>
          axios
            .delete(`/api/content/deleteDataset/${ct}`)
            .then(() => ({ contentType: ct }))
        )
      );
      const results = [];
      const errors = [];
      settled.forEach((r, i) => {
        const contentType = contentTypes[i];
        if (r.status === "fulfilled") {
          results.push({ contentType });
        } else {
          errors.push({
            contentType,
            error:
              r.reason?.response?.data?.error ||
              r.reason?.message ||
              "Unknown error",
          });
        }
      });
      return {
        results,
        errors,
        success: results.length,
        failed: errors.length,
      };
    },
    {
      onSuccess: (_res, { contentTypes }) => {
        queryClient.invalidateQueries(["tci", "tpls"]);
        queryClient.invalidateQueries(["allTpls"]);
        contentTypes.forEach((ct) => {
          queryClient.removeQueries(["tpl", ct]);
          queryClient.removeQueries(["tci", "tpl", ct]);
        });
      },
    }
  );
};
